import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type { IncidentBrief } from "@facet/core";
import {
  createEmailNotificationRoute,
  createOperation,
  createService,
  createTestIncident,
  loadChanges,
  loadChangeVerification,
  loadCoverage,
  loadIncident,
  loadIncidents,
  loadNotificationRoutes,
  type CoverageRecord,
  type ChangeVerificationDetail,
  type ChangeVerificationSummary,
  type IncidentDetail,
  type NotificationDeliveryStatus,
  type NotificationRouteRecord,
  type TestIncidentResult,
} from "./api.js";
import { signOut } from "./auth.js";
import { FacetButton, FacetInput, FacetSelect, FacetTextarea } from "./ui.js";

type ConnectionState = "checking" | "reachable" | "unreachable";
type ViewState = "loading" | "declare" | "coverage" | "incidents" | "changes";

interface OperationDraft {
  serviceName: string;
  operationName: string;
  environment: string;
  targetUrl: string;
  method: "GET" | "HEAD";
  expectedStatus: string;
  intervalSeconds: string;
  successCriteria: string;
}

const initialDraft: OperationDraft = {
  serviceName: "",
  operationName: "",
  environment: "production",
  targetUrl: "",
  method: "GET",
  expectedStatus: "200",
  intervalSeconds: "60",
  successCriteria: "",
};

function operationKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "operation";
}

function blockerLabel(blocker: string): string {
  const labels: Record<string, string> = {
    evidence_source_required: "Connect an evidence source",
    failure_quorum_exceeds_sources: "Failure rule needs a source",
    success_quorum_exceeds_sources: "Success rule needs a source",
  };
  return labels[blocker] ?? blocker.replaceAll("_", " ");
}

function formatTimestamp(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

type VerificationDisplayState = "passed" | "failed" | "verifying" | "inconclusive" | "not_covered";

function verificationState(summary: ChangeVerificationSummary): VerificationDisplayState {
  if (summary.truncated) return "inconclusive";
  const states = summary.receipts.map((item) => item.receipt.state);
  if (states.length === 0) return "not_covered";
  if (states.includes("failed")) return "failed";
  if (states.includes("inconclusive")) return "inconclusive";
  if (states.includes("awaiting_completion")) return "verifying";
  return "passed";
}

export function App() {
  const [connection, setConnection] = useState<ConnectionState>("checking");
  const [view, setView] = useState<ViewState>("loading");
  const [coverage, setCoverage] = useState<readonly CoverageRecord[]>([]);
  const [notificationRoutes, setNotificationRoutes] = useState<readonly NotificationRouteRecord[]>([]);
  const [incidents, setIncidents] = useState<readonly IncidentBrief[]>([]);
  const [selectedIncidentId, setSelectedIncidentId] = useState<string>();
  const [incidentDetail, setIncidentDetail] = useState<IncidentDetail>();
  const [incidentLoading, setIncidentLoading] = useState(false);
  const [incidentError, setIncidentError] = useState<string>();
  const [changes, setChanges] = useState<readonly ChangeVerificationSummary[]>([]);
  const [selectedChangeId, setSelectedChangeId] = useState<string>();
  const [changeDetail, setChangeDetail] = useState<ChangeVerificationDetail>();
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeError, setChangeError] = useState<string>();
  const [selectedId, setSelectedId] = useState<string>();
  const [draft, setDraft] = useState<OperationDraft>(initialDraft);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const mutationKeys = useRef(new Map<string, string>());

  const selected = useMemo(
    () => coverage.find((item) => item.declaration.declarationId === selectedId) ?? coverage[0],
    [coverage, selectedId],
  );

  useEffect(() => {
    const controller = new AbortController();

    void Promise.all([
      fetch("/healthz", { signal: controller.signal }),
      loadCoverage(controller.signal),
      loadNotificationRoutes(controller.signal),
    ]).then(([health, items, routes]) => {
      setConnection(health.ok ? "reachable" : "unreachable");
      setCoverage(items);
      setNotificationRoutes(routes);
      setSelectedId(items[0]?.declaration.declarationId);
      setView(items.length > 0 ? "coverage" : "declare");
    }).catch((caught: unknown) => {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setConnection("unreachable");
      setError(caught instanceof Error ? caught.message : "Facet could not load coverage.");
      setView("declare");
    });

    void loadIncidents(controller.signal).then((loadedIncidents) => {
      setIncidents(loadedIncidents);
      setSelectedIncidentId(loadedIncidents[0]?.incidentId);
    }).catch((caught: unknown) => {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setIncidentError(caught instanceof Error ? caught.message : "Facet could not load incidents.");
    });

    void loadChanges(controller.signal).then((loadedChanges) => {
      setChanges(loadedChanges);
      setSelectedChangeId(loadedChanges[0]?.change.changeId);
    }).catch((caught: unknown) => {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setChangeError(caught instanceof Error ? caught.message : "Facet could not load changes.");
    });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (view !== "incidents" || !selectedIncidentId) {
      setIncidentDetail(undefined);
      return;
    }
    const controller = new AbortController();
    setIncidentLoading(true);
    setIncidentDetail(undefined);
    setIncidentError(undefined);
    void loadIncident(selectedIncidentId, controller.signal)
      .then(setIncidentDetail)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        setIncidentError(caught instanceof Error ? caught.message : "Facet could not load this incident.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setIncidentLoading(false);
      });
    return () => controller.abort();
  }, [selectedIncidentId, view]);

  useEffect(() => {
    if (view !== "changes" || !selectedChangeId) {
      setChangeDetail(undefined);
      return;
    }
    const controller = new AbortController();
    setChangeLoading(true);
    setChangeDetail(undefined);
    setChangeError(undefined);
    void loadChangeVerification(selectedChangeId, controller.signal)
      .then(setChangeDetail)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        setChangeError(caught instanceof Error ? caught.message : "Facet could not load this verification receipt.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setChangeLoading(false);
      });
    return () => controller.abort();
  }, [selectedChangeId, view]);

  function updateDraft<Key extends keyof OperationDraft>(key: Key, value: OperationDraft[Key]): void {
    setDraft((current) => ({ ...current, [key]: value }));
    setError(undefined);
  }

  function mutationKey(kind: "service" | "operation" | "notification-route" | "test-incident", fingerprint: string): string {
    const key = `${kind}:${fingerprint}`;
    const existing = mutationKeys.current.get(key);
    if (existing) return existing;
    const created = crypto.randomUUID();
    mutationKeys.current.set(key, created);
    return created;
  }

  async function addNotificationRoute(monitorId: string, destination: string): Promise<void> {
    const normalizedDestination = destination.trim().toLowerCase();
    const route = await createEmailNotificationRoute(
      monitorId,
      normalizedDestination,
      mutationKey("notification-route", JSON.stringify({ monitorId, destination: normalizedDestination })),
    );
    setNotificationRoutes((current) => {
      const existing = current.findIndex((item) => item.id === route.id);
      if (existing < 0) return [...current, route];
      return current.map((item, index) => index === existing
        ? { ...route, latestDelivery: item.latestDelivery }
        : item);
    });
  }

  async function sendTestIncident(monitorId: string): Promise<TestIncidentResult> {
    const fingerprint = monitorId;
    const result = await createTestIncident(
      monitorId,
      mutationKey("test-incident", fingerprint),
    );
    setNotificationRoutes(await loadNotificationRoutes());
    mutationKeys.current.delete(`test-incident:${fingerprint}`);
    return result;
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(undefined);
    try {
      const serviceName = draft.serviceName.trim();
      const operationInput = {
        operationName: draft.operationName.trim(),
        operationKey: operationKey(draft.operationName),
        environment: draft.environment,
        targetUrl: draft.targetUrl.trim(),
        method: draft.method,
        expectedStatus: Number(draft.expectedStatus),
        intervalSeconds: Number(draft.intervalSeconds),
        successCriteria: draft.successCriteria.trim(),
      } as const;
      const service = await createService(serviceName, mutationKey("service", serviceName));
      const record = await createOperation(
        service.id,
        operationInput,
        mutationKey("operation", JSON.stringify({ serviceId: service.id, ...operationInput })),
      );
      setCoverage((current) => [...current, record]);
      setSelectedId(record.declaration.declarationId);
      setView("coverage");
      setDraft(initialDraft);
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : "Facet could not declare this operation.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="Facet Run home">
          <span className="brand-mark" aria-hidden="true">F</span>
          <span>Facet Run</span>
        </a>
        <nav aria-label="Primary navigation">
          <FacetButton
            className={`nav-item ${view !== "incidents" && view !== "changes" ? "nav-item--active" : ""}`}
            onClick={() => setView(coverage.length > 0 ? "coverage" : "declare")}
          >Coverage</FacetButton>
          <FacetButton
            className={`nav-item ${view === "incidents" ? "nav-item--active" : ""}`}
            onClick={() => {
              setSelectedIncidentId((current) => current ?? incidents[0]?.incidentId);
              setView("incidents");
            }}
          >Incidents</FacetButton>
          <FacetButton
            className={`nav-item ${view === "changes" ? "nav-item--active" : ""}`}
            onClick={() => {
              setSelectedChangeId((current) => current ?? changes[0]?.change.changeId);
              setView("changes");
            }}
          >Changes</FacetButton>
        </nav>
        <div className="topbar-actions">
          <span className={`connection connection--${connection}`} role="status">
            <span className="connection-dot" aria-hidden="true" />
            {connection === "checking" ? "Checking" : connection}
          </span>
          <FacetButton className="sign-out" onClick={() => {
            void signOut().then(() => window.location.assign("/login"));
          }}>Sign out</FacetButton>
        </div>
      </header>

      <main id={view === "incidents" ? "incidents" : view === "changes" ? "changes" : "coverage"} className="workspace">
        <aside className="index-pane" aria-label={view === "incidents" ? "Incidents" : view === "changes" ? "Changes" : "Declared operations"}>
          {view === "changes" ? (
            <>
              <div className="pane-heading">
                <div>
                  <p className="eyebrow">Change index</p>
                  <h1>Verification receipts</h1>
                </div>
                <span className="count">{changes.length.toString().padStart(2, "0")}</span>
              </div>
              {changeError && changes.length === 0 ? (
                <p className="index-empty" role="alert">{changeError}</p>
              ) : changes.length === 0 ? (
                <p className="index-empty">No signed change has been received yet.</p>
              ) : (
                <ul className="operation-index change-index">
                  {changes.map((summary) => {
                    const state = verificationState(summary);
                    return (
                      <li key={summary.change.changeId}>
                        <FacetButton
                          className={summary.change.changeId === selectedChangeId ? "selected" : ""}
                          onClick={() => setSelectedChangeId(summary.change.changeId)}
                        >
                          <span>{summary.change.version ?? summary.change.kind.replaceAll("_", " ")}</span>
                          <small>{summary.change.source} / {summary.change.environment}</small>
                          <strong className={`receipt-state--${state}`}>{state.replaceAll("_", " ")}</strong>
                        </FacetButton>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          ) : view === "incidents" ? (
            <>
              <div className="pane-heading">
                <div>
                  <p className="eyebrow">Incident index</p>
                  <h1>Evidence briefs</h1>
                </div>
                <span className="count">{incidents.length.toString().padStart(2, "0")}</span>
              </div>
              {incidentError && incidents.length === 0 ? (
                <p className="index-empty" role="alert">{incidentError}</p>
              ) : incidents.length === 0 ? (
                <p className="index-empty">No evidence-backed incidents yet.</p>
              ) : (
                <ul className="operation-index incident-index">
                  {incidents.map((incident) => (
                    <li key={incident.incidentId}>
                      <FacetButton
                        className={incident.incidentId === selectedIncidentId ? "selected" : ""}
                        onClick={() => setSelectedIncidentId(incident.incidentId)}
                      >
                        <span>{incident.summary}</span>
                        <small>{incident.scope.operation} / {incident.scope.environment}</small>
                        <strong>{incident.state}</strong>
                      </FacetButton>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <>
          <div className="pane-heading">
            <div>
              <p className="eyebrow">Coverage index</p>
              <h1>Production operations</h1>
            </div>
            <span className="count">{coverage.length.toString().padStart(2, "0")}</span>
          </div>

          {coverage.length === 0 ? (
            <p className="index-empty">Nothing declared. Health remains unknown.</p>
          ) : (
            <ul className="operation-index">
              {coverage.map((item) => (
                <li key={item.declaration.declarationId}>
                  <FacetButton
                    className={item.declaration.declarationId === selected?.declaration.declarationId ? "selected" : ""}
                    onClick={() => {
                      setSelectedId(item.declaration.declarationId);
                      setView("coverage");
                    }}
                  >
                    <span>{item.declaration.operationName}</span>
                    <small>{item.service.name} / {item.declaration.scope.environment}</small>
                    <strong>{item.compilation.readiness === "ready" ? "Connected" : "Needs source"}</strong>
                  </FacetButton>
                </li>
              ))}
            </ul>
          )}

          <FacetButton className="text-action" onClick={() => setView("declare")}>
            <span aria-hidden="true">＋</span> Declare operation
          </FacetButton>
            </>
          )}
        </aside>

        <section className="work-pane">
          {view === "loading" ? (
            <div className="loading-state" role="status">Loading coverage…</div>
          ) : view === "changes" ? (
            changeError && changes.length === 0 ? (
              <div className="loading-state" role="alert">{changeError}</div>
            ) : changes.length === 0 ? (
              <div className="loading-state">No verification receipt exists. Facet creates one when a signed change matches declared coverage.</div>
            ) : changeLoading ? (
              <div className="loading-state" role="status">Loading verification receipt…</div>
            ) : changeDetail ? (
              <ChangeVerificationView detail={changeDetail} />
            ) : changeError ? (
              <div className="loading-state" role="alert">{changeError}</div>
            ) : null
          ) : view === "incidents" ? (
            incidentError && incidents.length === 0 ? (
              <div className="loading-state" role="alert">{incidentError}</div>
            ) : incidents.length === 0 ? (
              <div className="loading-state">No incident brief exists. Facet opens one only from admitted failure evidence.</div>
            ) : incidentLoading ? (
              <div className="loading-state" role="status">Loading incident evidence…</div>
            ) : incidentDetail ? (
              <IncidentDetailView detail={incidentDetail} />
            ) : incidentError ? (
              <div className="loading-state" role="alert">{incidentError}</div>
            ) : null
          ) : view === "declare" ? (
            <DeclarationForm
              draft={draft}
              error={error}
              submitting={submitting}
              onChange={updateDraft}
              onSubmit={submit}
              onCancel={coverage.length > 0 ? () => setView("coverage") : undefined}
            />
          ) : selected ? (
            <CoverageDetail
              record={selected}
              notificationRoutes={notificationRoutes.filter((route) => (
                route.monitorId === selected.compilation.monitor.monitorId
              ))}
              onAddNotificationRoute={addNotificationRoute}
              onSendTestIncident={sendTestIncident}
            />
          ) : null}
        </section>
      </main>

      <footer>
        <span>Contract v1</span>
        <span>PostgreSQL-backed</span>
        <span>{window.location.host}</span>
      </footer>
    </div>
  );
}

interface DeclarationFormProps {
  draft: OperationDraft;
  error?: string | undefined;
  submitting: boolean;
  onChange: <Key extends keyof OperationDraft>(key: Key, value: OperationDraft[Key]) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onCancel?: (() => void) | undefined;
}

interface TimelineEntry {
  id: string;
  at: string;
  kind: "change" | "observation" | "evaluation";
  title: string;
  detail: string;
  state: string;
}

function ChangeVerificationView({ detail }: { detail: ChangeVerificationDetail }) {
  const summary: ChangeVerificationSummary = {
    change: detail.change,
    receipts: detail.items,
    truncated: detail.truncated,
  };
  const state = verificationState(summary);
  const title = detail.change.version
    ? `Release ${detail.change.version}`
    : detail.change.kind.replaceAll("_", " ");

  return (
    <article className="receipt-detail">
      <header className="detail-header receipt-header">
        <div>
          <p className="eyebrow">Change / {detail.change.changeId.slice(-12)}</p>
          <h2>{title}</h2>
          <p>Reported by {detail.change.source} · {formatTimestamp(detail.change.sourceEventAt)}</p>
        </div>
        <span className={`status-label status-label--receipt-${state}`}>{state.replaceAll("_", " ")}</span>
      </header>

      <div className="contract-strip receipt-strip" aria-label="Change facts">
        <div><span>Kind</span><strong>{detail.change.kind.replaceAll("_", " ")}</strong></div>
        <div><span>Environment</span><strong>{detail.change.environment}</strong></div>
        <div><span>Operations</span><strong>{detail.items.length}</strong></div>
        <div><span>Completed</span><strong>{detail.change.completedAt ? "reported" : "unknown"}</strong></div>
      </div>

      <section className="receipt-path" aria-labelledby="receipt-heading">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Post-change verification</p>
            <h3 id="receipt-heading">What the cited operation evidence proves.</h3>
          </div>
          <code>{detail.items.length.toString().padStart(2, "0")} receipts</code>
        </div>
        {detail.items.length === 0 ? (
          <div className="receipt-uncovered">
            <strong>No declared operation matched this change.</strong>
            <p>Facet received the change, but cannot claim it passed or failed without matching coverage.</p>
          </div>
        ) : (
          <>
          {detail.truncated ? (
            <p className="receipt-truncated" role="status">Receipt output is truncated. Facet will not summarize this change as passed.</p>
          ) : null}
          <ol className="receipt-list">
            {detail.items.map((item) => (
              <li key={item.receipt.receiptId} className={`receipt-card receipt-card--${item.receipt.state}`}>
                <div className="receipt-card-heading">
                  <div>
                    <p className="eyebrow">{item.scope.environment} / {item.receipt.monitorId.slice(0, 12)}</p>
                    <h4>{item.scope.operation}</h4>
                  </div>
                  <strong>{item.receipt.state.replaceAll("_", " ")}</strong>
                </div>
                <p className="receipt-reason">{item.receipt.reason.replaceAll("_", " ")}</p>
                <dl>
                  <div><dt>Started</dt><dd>{formatTimestamp(item.receipt.startedAt)}</dd></div>
                  <div><dt>{item.receipt.decidedAt ? "Decided" : "Deadline"}</dt><dd>{formatTimestamp(item.receipt.decidedAt ?? item.receipt.deadlineAt)}</dd></div>
                  <div><dt>Evaluations</dt><dd>{item.evaluations.length}</dd></div>
                  <div><dt>Observations</dt><dd>{item.observations.length}</dd></div>
                </dl>
                {item.receipt.missingEvidence.length > 0 ? (
                  <p className="receipt-missing">Missing: {item.receipt.missingEvidence.map((value) => value.replaceAll("_", " ")).join(" · ")}</p>
                ) : (
                  <p className="receipt-complete">Decision is pinned to the cited evidence and policy.</p>
                )}
              </li>
            ))}
          </ol>
          </>
        )}
      </section>

      <div className="detail-columns receipt-columns">
        <section>
          <p className="eyebrow">Change boundary</p>
          <h3>{detail.change.source}</h3>
          <p>Facet verifies declared operations after this event. Time proximity does not prove the change caused a failure.</p>
        </section>
        <section>
          <p className="eyebrow">Historical truth</p>
          <h3>Terminal receipts do not turn green later.</h3>
          <p>Recovery is recorded by the incident timeline; it does not rewrite the post-change result.</p>
        </section>
      </div>
    </article>
  );
}

function IncidentDetailView({ detail }: { detail: IncidentDetail }) {
  const { incident } = detail;
  const timeline: TimelineEntry[] = [
    ...detail.changes.map((change) => ({
      id: change.changeId,
      at: change.sourceEventAt,
      kind: "change" as const,
      title: change.version ? `Release ${change.version}` : change.kind.replaceAll("_", " "),
      detail: `${change.source} · Nearby change, not confirmed cause`,
      state: change.kind,
    })),
    ...detail.observations.map((observation) => ({
      id: observation.observationId,
      at: observation.sourceEventAt,
      kind: "observation" as const,
      title: observation.reason.replaceAll("_", " "),
      detail: `${observation.source.provider} · ${observation.source.resourceId}`,
      state: observation.status,
    })),
    ...detail.evaluations.map((evaluation) => ({
      id: evaluation.evaluationId,
      at: evaluation.evaluatedAt,
      kind: "evaluation" as const,
      title: evaluation.reason.replaceAll("_", " "),
      detail: `${evaluation.evidenceIds.length} cited observation${evaluation.evidenceIds.length === 1 ? "" : "s"}`,
      state: evaluation.state,
    })),
  ].sort((left, right) => left.at.localeCompare(right.at) || left.id.localeCompare(right.id));
  const citedRecordCount = detail.observations.length + detail.evaluations.length + detail.changes.length;

  return (
    <article className="incident-detail">
      <header className="detail-header incident-header">
        <div>
          <p className="eyebrow">Incident / {incident.incidentId.slice(-12)}</p>
          <h2>{incident.summary}</h2>
          <p>Opened {formatTimestamp(incident.openedAt)} · {incident.scope.operation} / {incident.scope.environment}</p>
        </div>
        <span className={`status-label status-label--incident-${incident.state}`}>{incident.state}</span>
      </header>

      <div className="contract-strip incident-strip" aria-label="Incident facts">
        <div><span>State</span><strong>{incident.state}</strong></div>
        <div><span>Cited records</span><strong>{citedRecordCount}</strong></div>
        <div><span>Nearby changes</span><strong>{detail.changes.length}</strong></div>
        <div><span>Policy</span><strong>{incident.policyVersion}</strong></div>
      </div>

      <section className="first-action" aria-labelledby="first-action-heading">
        <p className="eyebrow">First action</p>
        <h3 id="first-action-heading">{incident.firstAction}</h3>
        <p>Start with cited evidence. A nearby change is context, not proof of cause.</p>
      </section>

      <section className="incident-timeline" aria-labelledby="timeline-heading">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Evidence timeline</p>
            <h3 id="timeline-heading">What Facet observed and inferred.</h3>
          </div>
          <code>oldest first</code>
        </div>
        {timeline.length === 0 ? (
          <p className="timeline-empty">No cited evidence is available.</p>
        ) : (
          <ol>
            {timeline.map((entry, index) => (
              <li key={`${entry.kind}:${entry.id}`} className={`timeline-entry timeline-entry--${entry.kind}`}>
                <span className="timeline-sequence">{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <div className="timeline-title">
                    <strong>{entry.title}</strong>
                    <span>{entry.kind}</span>
                  </div>
                  <p>{entry.detail}</p>
                  <time dateTime={entry.at}>{formatTimestamp(entry.at)}</time>
                </div>
                <span className={`timeline-state timeline-state--${entry.state}`}>{entry.state.replaceAll("_", " ")}</span>
              </li>
            ))}
          </ol>
        )}
      </section>

      <div className="detail-columns incident-columns">
        <section>
          <p className="eyebrow">Unknowns</p>
          <h3>Evidence Facet still lacks</h3>
          {incident.missingEvidence.length > 0 ? (
            <ul>{incident.missingEvidence.map((item) => <li key={item}>{item.replaceAll("_", " ")}</li>)}</ul>
          ) : <p>No missing input is declared for the latest decision.</p>}
        </section>
        <section>
          <p className="eyebrow">Scope boundary</p>
          <h3>{incident.scope.operation}</h3>
          <p>This brief proves only the declared operation in {incident.scope.environment}. It is not application-wide health.</p>
        </section>
      </div>
    </article>
  );
}

function DeclarationForm({ draft, error, submitting, onChange, onSubmit, onCancel }: DeclarationFormProps) {
  return (
    <div className="declare-layout">
      <div className="declare-copy">
        <p className="eyebrow">Declare / 01</p>
        <h2>What must work after every release?</h2>
        <p>Define one narrow operation. Facet stores the boundary now and will not claim health until an authorized evidence source is attached.</p>
        <ol className="mini-rail" aria-label="Declaration lifecycle">
          <li className="active"><span>01</span><div><strong>Declare</strong><small>Intent and assertion</small></div></li>
          <li><span>02</span><div><strong>Connect</strong><small>External evidence source</small></div></li>
          <li><span>03</span><div><strong>Observe</strong><small>Fresh scoped results</small></div></li>
        </ol>
      </div>

      <form className="declaration-form" onSubmit={(event) => void onSubmit(event)}>
        <div className="form-section-heading">
          <div>
            <span>Operation contract</span>
            <strong>Public HTTP / status assertion</strong>
          </div>
          <code>draft</code>
        </div>

        <div className="field-grid">
          <label>
            <span>Service</span>
            <FacetInput
              name="service-name"
              value={draft.serviceName}
              onChange={(event) => onChange("serviceName", event.target.value)}
              placeholder="Storefront…"
              autoComplete="off"
              required
              maxLength={120}
            />
          </label>
          <label>
            <span>Operation</span>
            <FacetInput
              name="operation-name"
              value={draft.operationName}
              onChange={(event) => onChange("operationName", event.target.value)}
              placeholder="Submit checkout…"
              autoComplete="off"
              required
              maxLength={120}
            />
          </label>
          <label>
            <span>Environment</span>
            <FacetSelect value={draft.environment} onChange={(event) => onChange("environment", event.target.value)}>
              <option value="production">Production</option>
              <option value="staging">Staging</option>
            </FacetSelect>
          </label>
          <label>
            <span>Method</span>
            <FacetSelect value={draft.method} onChange={(event) => onChange("method", event.target.value as "GET" | "HEAD")}>
              <option value="GET">GET</option>
              <option value="HEAD">HEAD</option>
            </FacetSelect>
          </label>
          <label className="wide">
            <span>Public HTTPS target</span>
            <FacetInput
              name="target-url"
              type="url"
              inputMode="url"
              spellCheck="false"
              value={draft.targetUrl}
              onChange={(event) => onChange("targetUrl", event.target.value)}
              placeholder="https://api.example.com/health/checkout…"
              required
              maxLength={2048}
            />
            <small>Credentials are not accepted in the URL. Private-network targets remain out of scope.</small>
          </label>
          <label>
            <span>Expected status</span>
            <FacetInput
              name="expected-status"
              type="number"
              inputMode="numeric"
              min="100"
              max="599"
              value={draft.expectedStatus}
              onChange={(event) => onChange("expectedStatus", event.target.value)}
              required
            />
          </label>
          <label>
            <span>Requested cadence</span>
            <FacetSelect value={draft.intervalSeconds} onChange={(event) => onChange("intervalSeconds", event.target.value)}>
              <option value="30">Every 30 seconds</option>
              <option value="60">Every minute</option>
              <option value="300">Every 5 minutes</option>
            </FacetSelect>
          </label>
          <label className="wide">
            <span>What does success prove?</span>
            <FacetTextarea
              name="success-criteria"
              rows={3}
              value={draft.successCriteria}
              onChange={(event) => onChange("successCriteria", event.target.value)}
              placeholder="A customer can reach the checkout readiness path and receive HTTP 200…"
              required
              maxLength={500}
            />
          </label>
        </div>

        {error ? <p className="form-error" role="alert">{error}</p> : null}

        <div className="form-actions">
          <span>Saved as unknown until evidence is connected.</span>
          <div>
            {onCancel ? <FacetButton className="button-secondary" onClick={onCancel}>Cancel</FacetButton> : null}
            <FacetButton className="button-primary" type="submit" disabled={submitting}>
              {submitting ? "Declaring…" : "Declare Operation"}
            </FacetButton>
          </div>
        </div>
      </form>
    </div>
  );
}

interface CoverageDetailProps {
  record: CoverageRecord;
  notificationRoutes: readonly NotificationRouteRecord[];
  onAddNotificationRoute: (monitorId: string, destination: string) => Promise<void>;
  onSendTestIncident: (monitorId: string) => Promise<TestIncidentResult>;
}

const deliveryLabels: Record<NotificationDeliveryStatus, string> = {
  pending: "Pending",
  attempting: "Sending",
  accepted: "Provider accepted",
  sent: "Provider sent",
  delayed: "Delayed",
  delivered: "Delivered",
  failed: "Failed",
  unknown: "Unknown",
};

function CoverageDetail({
  record,
  notificationRoutes,
  onAddNotificationRoute,
  onSendTestIncident,
}: CoverageDetailProps) {
  const assertion = record.declaration.probe.assertions[0];
  const source = record.compilation.monitor.sources[0];
  const connected = record.compilation.readiness === "ready" && source !== undefined;
  const sourceDescription = !source
    ? record.compilation.blockers.map(blockerLabel).join(" · ")
    : source.kind === "dispatched_probe"
      ? `${source.executorId} · Facet probe scheduled`
      : source.kind === "delegated_monitor"
        ? `${source.adapterId} · external monitor bound`
        : `${source.adapterId} · connected evidence`;
  const blindSpots = connected
    ? record.declaration.knownBlindSpots.filter((blindSpot) => blindSpot !== "No evidence source is connected yet.")
    : record.declaration.knownBlindSpots;
  const [destination, setDestination] = useState("");
  const [notificationError, setNotificationError] = useState<string>();
  const [savingNotification, setSavingNotification] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<string>();
  const [testError, setTestError] = useState<string>();
  const monitorId = record.compilation.monitor.monitorId;

  useEffect(() => {
    setDestination("");
    setNotificationError(undefined);
    setTestResult(undefined);
    setTestError(undefined);
  }, [monitorId]);

  async function submitNotificationRoute(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSavingNotification(true);
    setNotificationError(undefined);
    try {
      await onAddNotificationRoute(monitorId, destination);
      setDestination("");
    } catch (caught: unknown) {
      setNotificationError(caught instanceof Error ? caught.message : "Facet could not save this email route.");
    } finally {
      setSavingNotification(false);
    }
  }

  async function testNotificationPath(): Promise<void> {
    setSendingTest(true);
    setTestResult(undefined);
    setTestError(undefined);
    try {
      const result = await onSendTestIncident(monitorId);
      setTestResult(`Test incident sent to ${result.notificationCount} ${result.notificationCount === 1 ? "route" : "routes"}.`);
    } catch (caught: unknown) {
      setTestError(caught instanceof Error ? caught.message : "Facet could not send this test incident.");
    } finally {
      setSendingTest(false);
    }
  }

  return (
    <div className="coverage-detail">
      <header className="detail-header">
        <div>
          <p className="eyebrow">Coverage / {record.declaration.scope.environment}</p>
          <h2>{record.declaration.operationName}</h2>
          <p>{record.service.name} · declared {formatTimestamp(record.declaration.createdAt)}</p>
        </div>
        <span className={`status-label ${connected ? "status-label--connected" : "status-label--unknown"}`}>
          {connected ? "Awaiting evidence" : "Needs source"}
        </span>
      </header>

      <div className="contract-strip">
        <div><span>Method</span><strong>{record.declaration.probe.method}</strong></div>
        <div><span>Assertion</span><strong>{assertion ? `HTTP ${assertion.expected}` : "Not set"}</strong></div>
        <div><span>Cadence</span><strong>{record.declaration.probe.intervalSeconds}s</strong></div>
        <div><span>Sources</span><strong>{record.compilation.monitor.sources.length}</strong></div>
      </div>

      <section className="evidence-path" aria-labelledby="evidence-heading">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Evidence path</p>
            <h3 id="evidence-heading">
              {connected ? "Source connected. Awaiting fresh evidence." : "Declaration stored. Health is still unknown."}
            </h3>
          </div>
          <code>{record.compilation.monitor.monitorId.slice(0, 8)}</code>
        </div>
        <ol className="evidence-rail">
          <li className="complete">
            <span className="step-number">01</span>
            <div><h4>Declared</h4><p>{record.declaration.successCriteria}</p></div>
            <strong>Stored</strong>
          </li>
          <li className={connected ? "connected" : "blocked"}>
            <span className="step-number">02</span>
            <div>
              <h4>Evidence source</h4>
              <p>{sourceDescription}</p>
            </div>
            <strong>{connected ? "Connected" : "Required"}</strong>
          </li>
          <li>
            <span className="step-number">03</span>
            <div><h4>Evaluation</h4><p>No fresh independent observation can support a decision.</p></div>
            <strong>Unknown</strong>
          </li>
        </ol>
      </section>

      <div className="detail-columns">
        <section>
          <p className="eyebrow">Target</p>
          <a className="target-url" href={record.declaration.probe.targetUrl} target="_blank" rel="noreferrer">
            {record.declaration.probe.targetUrl}
          </a>
        </section>
        <section>
          <p className="eyebrow">Known blind spots</p>
          <ul>
            {blindSpots.map((blindSpot) => <li key={blindSpot}>{blindSpot}</li>)}
          </ul>
        </section>
      </div>

      <section className="notification-path" aria-labelledby="notification-heading">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Notification path</p>
            <h3 id="notification-heading">Route incident evidence to the right owner.</h3>
          </div>
          <code>{notificationRoutes.length.toString().padStart(2, "0")} routes</code>
        </div>

        {notificationRoutes.length > 0 ? (
          <ul className="notification-routes">
            {notificationRoutes.map((route) => (
              <li key={route.id}>
                <div>
                  <strong>{route.destination}</strong>
                  <span>Email · added {formatTimestamp(route.createdAt)}</span>
                </div>
                {route.latestDelivery ? (
                  <div className="delivery-evidence">
                    <strong className={`delivery-status delivery-status--${route.latestDelivery.status}`}>
                      {deliveryLabels[route.latestDelivery.status]}
                    </strong>
                    <span>{formatTimestamp(route.latestDelivery.occurredAt)}</span>
                  </div>
                ) : (
                  <div className="delivery-evidence">
                    <strong className="delivery-status">No delivery yet</strong>
                    <span>Waiting for an incident transition</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="notification-empty">
            No route is configured. Detection remains deterministic, but nobody will receive incident or recovery email.
          </p>
        )}

        <form className="notification-form" onSubmit={(event) => void submitNotificationRoute(event)}>
          <label htmlFor={`notification-email-${monitorId}`}>Owner email</label>
          <div>
            <FacetInput
              id={`notification-email-${monitorId}`}
              name="notification-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              spellCheck="false"
              value={destination}
              onChange={(event) => {
                setDestination(event.target.value);
                setNotificationError(undefined);
              }}
              placeholder="owner@company.com…"
              required
              maxLength={254}
              aria-invalid={notificationError ? "true" : "false"}
              aria-describedby={notificationError ? `notification-error-${monitorId}` : "notification-route-help"}
            />
            <FacetButton className="button-primary" type="submit" disabled={savingNotification}>
              {savingNotification ? "Adding…" : "Add Email Route"}
            </FacetButton>
          </div>
          {notificationError ? (
            <span id={`notification-error-${monitorId}`} className="field-error" role="alert">
              {notificationError}
            </span>
          ) : (
            <span id="notification-route-help">Email is sent only when an incident opens or recovers.</span>
          )}
        </form>

        <div className="notification-test">
          <div>
            <strong>Prove the response path</strong>
            <span>Sends a permanently labelled synthetic incident. Operation health is not changed.</span>
          </div>
          <FacetButton
            className="button-secondary"
            disabled={sendingTest || notificationRoutes.length === 0}
            onClick={() => void testNotificationPath()}
          >
            {sendingTest ? "Sending test…" : "Send test incident"}
          </FacetButton>
          {testResult ? <span className="test-result" role="status">{testResult}</span> : null}
          {testError ? <span className="field-error" role="alert">{testError}</span> : null}
        </div>
      </section>
    </div>
  );
}
